import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Badge, Alert, Spinner, Nav, Modal, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { getEncours } from './DataService';
import './Encours.css';

const BUCKET_INFO = {
  a_echoir: { label: 'À échoir', bg: 'secondary' },
  retard_1_30: { label: '1-30 jours', bg: 'warning' },
  retard_31_60: { label: '31-60 jours', bg: 'warning' },
  retard_61_90: { label: '61-90 jours', bg: 'danger' },
  retard_90_plus: { label: '90+ jours', bg: 'danger' },
};

const STATUT_INFO = {
  rouge: { emoji: '🔴', bg: 'danger' },
  orange: { emoji: '🟠', bg: 'warning' },
};

const monthNames = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
];

const formatEuros = (value) => `${(value || 0).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} €`;

const InfoTooltip = ({ text }) => (
  <OverlayTrigger placement="top" overlay={<Tooltip>{text}</Tooltip>}>
    <span className="text-muted" style={{ cursor: 'help', marginLeft: 4 }}>ⓘ</span>
  </OverlayTrigger>
);

// Relances automatiques (drip) déjà envoyées. « exclu » plutôt que 0 quand le
// client est exclu des relances : sinon un 0 laisse croire à un oubli du drip.
const formatRelances = (client) => {
  if (client.exclu_relance) {
    return <span className="text-muted" title="Client exclu des relances automatiques">exclu</span>;
  }
  if (!client.nb_relances) {
    return <span className="text-muted">0</span>;
  }
  // « 2026-10-02 » découpé plutôt que passé à `new Date` : pas de décalage de fuseau.
  const [a, m, j] = String(client.derniere_relance_le || '').split('-');
  return (
    <>
      <strong>{client.nb_relances}</strong>
      {j && <div className="text-muted small">dernière le {j}/{m}/{a}</div>}
    </>
  );
};

// « 2026-10-08 » ou « 2026-10-08 09:00 » en « 08/10/2026 » (« à 09:00 »), sans
// passer par `new Date`, qui décalerait selon le fuseau du navigateur.
const dateFr = (valeur) => {
  const [jour, heure] = String(valeur || '').split(' ');
  const [a, m, j] = jour.split('-');
  if (!j) return '—';
  return `${j}/${m}/${a}${heure ? ` à ${heure}` : ''}`;
};

// Prochaine relance automatique : la date, ou la raison pour laquelle il n'y en
// aura pas. Calculée par l'API avec la règle même du drip.
const formatProchaineRelance = (prochaine, exclu) => {
  if (exclu) {
    return <span className="text-muted">exclu</span>;
  }
  if (prochaine?.date) {
    return dateFr(prochaine.date);
  }
  return <span className="text-muted small">{prochaine?.motif || '—'}</span>;
};

const formatClientDisplay = (client) => {
  if (client.client_company && client.client_company !== client.client_name) {
    return `${client.client_company} — ${client.client_name}`;
  }
  return client.client_name;
};

const Encours = () => {
  const [invoices, setInvoices] = useState([]);
  const [byClient, setByClient] = useState([]);
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('a-traiter');
  const [selectedClient, setSelectedClient] = useState(null);

  useEffect(() => {
    fetchEncours();
  }, []);

  const fetchEncours = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getEncours();
      const sorted = [...(data.invoices || [])].sort((a, b) => b.jours_retard - a.jours_retard);
      setInvoices(sorted);
      setByClient(data.by_client || []);
      setTotals(data.totals || null);
    } catch (err) {
      setError('Erreur lors de la récupération de l\'encours');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getBucketBadge = (tranche, label) => {
    const info = BUCKET_INFO[tranche] || { label, bg: 'secondary' };
    return <Badge bg={info.bg}>{label || info.label}</Badge>;
  };

  const getStatutBadge = (statut) => {
    const info = STATUT_INFO[statut];
    if (!info) return <span className="text-muted">—</span>;
    return <Badge bg={info.bg}>{info.emoji}</Badge>;
  };

  // "À traiter" = clients ayant du retard (statut rouge/orange), du plus urgent au moins urgent.
  const aTraiter = [...byClient]
    .filter((c) => c.statut)
    .sort((a, b) => b.jours_retard_max - a.jours_retard_max);

  // Pas d'automatisation pour l'instant : au-delà de 30j on privilégie l'appel (si un
  // numéro est connu), sinon une relance par email au comptable si renseigné, sinon au contact.
  const getActionButton = (client) => {
    if (client.jours_retard_max >= 30 && client.client_phone) {
      return <a href={`tel:${client.client_phone}`} className="btn btn-sm btn-outline-danger">📞 Appeler</a>;
    }
    const email = client.client_email_comptable || client.client_email;
    if (email) {
      return <a href={`mailto:${email}`} className="btn btn-sm btn-outline-primary">✉️ Relancer</a>;
    }
    return <span className="text-muted">—</span>;
  };

  return (
    <Container fluid className="encours-container py-4">
      <Row className="mb-4">
        <Col>
          <h2>Encours</h2>
          <p className="text-muted">Vieillissement des factures mensuelles impayées (clients en compte), basé sur la vraie date d'échéance</p>
        </Col>
      </Row>

      {error && <Alert variant="danger">{error}</Alert>}

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Chargement...</span>
          </Spinner>
        </div>
      ) : (
        <>
          {totals && (
            <>
              <Row className="mb-3">
                <Col md={6}>
                  <Card className="text-center border-danger h-100">
                    <Card.Body>
                      <h6 className="text-muted text-uppercase mb-2">
                        🔴 Clients à traiter
                        <InfoTooltip text="Clients ayant au moins une facture dont l'échéance est dépassée — ce sont les dossiers à relancer en priorité aujourd'hui." />
                      </h6>
                      <h2 className="mb-0 text-danger">{totals.clients_en_retard}</h2>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={6}>
                  <Card className="text-center h-100">
                    <Card.Body>
                      <h6 className="text-muted text-uppercase mb-2">
                        Clients &gt;30j
                        <InfoTooltip text="Clients ayant au moins une facture en retard de plus de 30 jours — cas plus urgents, à prioriser sur les simples relances 1-30j." />
                      </h6>
                      <h2 className="mb-0">{totals.clients_plus_30j}</h2>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={4}>
                  <Card className="text-center h-100">
                    <Card.Body>
                      <h6 className="text-muted text-uppercase mb-2">
                        DSO
                        <InfoTooltip text="Nombre de jours de chiffre d'affaires immobilisés en créances clients. Cible : 30-40 jours (délai de paiement normal). Au-delà, on s'éloigne de l'objectif — signe d'un encours qui s'accumule plus vite qu'il n'est recouvré." />
                      </h6>
                      <h2 className="mb-0">{totals.dso != null ? `${totals.dso} j` : '—'}</h2>
                      <p className="text-muted small mb-0">Jours de CA immobilisés en créances</p>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={4}>
                  <Card className="text-center h-100">
                    <Card.Body>
                      <h6 className="text-muted text-uppercase mb-2">
                        Délai moyen de paiement
                        <InfoTooltip text="Sur les factures réellement payées ces 90 derniers jours : nombre moyen de jours entre l'échéance et le paiement effectif. Négatif = payé en avance, positif = payé en retard. Cible : proche de 0." />
                      </h6>
                      <h2 className="mb-0">{totals.delai_moyen_paiement != null ? `${totals.delai_moyen_paiement} j` : '—'}</h2>
                      <p className="text-muted small mb-0">Entre échéance et paiement effectif (90j)</p>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={4}>
                  <Card className="text-center h-100">
                    <Card.Body>
                      <h6 className="text-muted text-uppercase mb-2">
                        Taux de paiement à échéance
                        <InfoTooltip text="Pourcentage des factures payées ces 90 derniers jours qui l'ont été au plus tard à leur date d'échéance. Plus c'est élevé, mieux c'est — cible : proche de 100%." />
                      </h6>
                      <h2 className="mb-0">{totals.taux_paiement_echeance != null ? `${totals.taux_paiement_echeance}%` : '—'}</h2>
                      <p className="text-muted small mb-0">Factures payées à temps (90j)</p>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={4}>
                  <Card className="text-center border-dark h-100">
                    <Card.Body>
                      <h6 className="text-muted text-uppercase mb-2">
                        Encours total
                        <InfoTooltip text="Somme de toutes les factures mensuelles non encore payées — à échoir ou en retard confondus. C'est tout ce que les clients en compte doivent actuellement." />
                      </h6>
                      <h2 className="mb-0">{formatEuros(totals.encours_total)}</h2>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={4}>
                  <Card className="text-center border-danger h-100">
                    <Card.Body>
                      <h6 className="text-muted text-uppercase mb-2">
                        Encours en retard
                        <InfoTooltip text="Part de l'encours total dont l'échéance est déjà dépassée — l'argent réellement en retard de paiement, hors factures pas encore dues (à échoir)." />
                      </h6>
                      <h2 className="mb-0 text-danger">{formatEuros(totals.encours_en_retard)}</h2>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={4}>
                  <Card className="text-center h-100">
                    <Card.Body>
                      <h6 className="text-muted text-uppercase mb-2">
                        À échoir
                        <InfoTooltip text="Factures déjà émises mais dont la date d'échéance n'est pas encore passée — normal, aucune action à mener pour l'instant." />
                      </h6>
                      <h2 className="mb-0">{formatEuros(totals.a_echoir)}</h2>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={true}>
                  <Card className="text-center border-warning h-100">
                    <Card.Body>
                      <h6 className="text-muted text-uppercase mb-2">1-30 jours</h6>
                      <h4 className="mb-0 text-warning">{formatEuros(totals.retard_1_30)}</h4>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={true}>
                  <Card className="text-center border-warning h-100">
                    <Card.Body>
                      <h6 className="text-muted text-uppercase mb-2">31-60 jours</h6>
                      <h4 className="mb-0 text-warning">{formatEuros(totals.retard_31_60)}</h4>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={true}>
                  <Card className="text-center border-danger h-100">
                    <Card.Body>
                      <h6 className="text-muted text-uppercase mb-2">61-90 jours</h6>
                      <h4 className="mb-0 text-danger">{formatEuros(totals.retard_61_90)}</h4>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={true}>
                  <Card className="text-center border-danger h-100">
                    <Card.Body>
                      <h6 className="text-muted text-uppercase mb-2">90+ jours</h6>
                      <h4 className="mb-0 text-danger">{formatEuros(totals.retard_90_plus)}</h4>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              <Row className="mb-4">
                <Col>
                  <Card>
                    <Card.Body>
                      <Card.Title className="h6 text-uppercase text-muted mb-3">Encours échu par tranche</Card.Title>
                      {[
                        { label: '90j+', value: totals.retard_90_plus, color: '#8b0000' },
                        { label: '61-90j', value: totals.retard_61_90, color: '#dc3545' },
                        { label: '31-60j', value: totals.retard_31_60, color: '#fd7e14' },
                        { label: '1-30j', value: totals.retard_1_30, color: '#ffc107' },
                      ].map((bucket) => {
                        const max = Math.max(totals.retard_1_30, totals.retard_31_60, totals.retard_61_90, totals.retard_90_plus, 1);
                        const widthPct = (bucket.value / max) * 100;
                        return (
                          <div key={bucket.label} className="d-flex align-items-center mb-2">
                            <div style={{ width: 60 }} className="text-muted small">{bucket.label}</div>
                            <div className="flex-grow-1 mx-2">
                              <div
                                style={{
                                  width: `${widthPct}%`,
                                  minWidth: bucket.value > 0 ? '4px' : 0,
                                  height: 18,
                                  backgroundColor: bucket.color,
                                  borderRadius: 3,
                                }}
                              />
                            </div>
                            <div style={{ width: 90 }} className="text-end small">{formatEuros(bucket.value)}</div>
                          </div>
                        );
                      })}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              {byClient.length > 0 && (
                <Row className="mb-4">
                  <Col>
                    <Card>
                      <Card.Body>
                        <Card.Title className="h6 text-uppercase text-muted mb-3">🔥 Top impayés</Card.Title>
                        {byClient.slice(0, 5).map((client) => (
                          <div key={client.client_id} className="d-flex justify-content-between align-items-start border-bottom py-2">
                            <span>
                              {client.client_name}
                              {client.client_company && client.client_company !== client.client_name && (
                                <span className="text-muted"> — {client.client_company}</span>
                              )}
                              {client.client_email_comptable && (
                                <div className="text-muted small">✉️ {client.client_email_comptable}</div>
                              )}
                            </span>
                            <span className="text-nowrap ms-3">
                              <strong>{formatEuros(client.encours)}</strong>
                              {client.jours_retard_max > 0 && (
                                <span className="text-muted ms-2">{client.jours_retard_max}j</span>
                              )}
                            </span>
                          </div>
                        ))}
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              )}
            </>
          )}

          <Nav variant="tabs" className="mb-3" activeKey={activeTab} onSelect={(key) => setActiveTab(key)}>
            <Nav.Item>
              <Nav.Link eventKey="a-traiter">À traiter aujourd'hui</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="factures">Factures</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="clients">Clients</Nav.Link>
            </Nav.Item>
          </Nav>

          {activeTab === 'a-traiter' ? (
            <Card>
              <Card.Body className="p-0">
                {aTraiter.length === 0 ? (
                  <Alert variant="success" className="m-4">
                    Rien à traiter aujourd'hui — aucun client en retard 🎉
                  </Alert>
                ) : (
                  <div className="table-responsive">
                    <Table striped hover className="mb-0">
                      <thead className="table-dark">
                        <tr>
                          <th>Client</th>
                          <th>En retard</th>
                          <th>Retard</th>
                          <th>Relances mail</th>
                          <th>Prochaine relance</th>
                          <th>Motif</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {aTraiter.map((client) => (
                          <tr key={client.client_id}>
                            <td role="button" style={{ cursor: 'pointer' }} onClick={() => setSelectedClient(client)}>
                              {formatClientDisplay(client)}
                            </td>
                            {/* Le montant échu seul : les factures pas encore à échéance
                                n'ont rien « à traiter ». L'encours total reste dans
                                l'onglet Clients. */}
                            <td><strong>{formatEuros(client.echu)}</strong></td>
                            <td>{client.jours_retard_max}j</td>
                            <td>{formatRelances(client)}</td>
                            <td>{formatProchaineRelance({ date: client.prochaine_relance }, client.exclu_relance)}</td>
                            <td>Impayé</td>
                            <td>{getActionButton(client)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </Card.Body>
            </Card>
          ) : activeTab === 'factures' ? (
            <Card>
              <Card.Body className="p-0">
                {invoices.length === 0 ? (
                  <Alert variant="success" className="m-4">
                    Aucune facture impayée — encours à zéro 🎉
                  </Alert>
                ) : (
                  <div className="table-responsive">
                    <Table striped hover className="mb-0">
                      <thead className="table-dark">
                        <tr>
                          <th>N° Facture</th>
                          <th>Client</th>
                          <th>Période</th>
                          <th>Montant TTC</th>
                          <th>Échéance</th>
                          <th>Retard</th>
                          <th>Tranche</th>
                        </tr>
                      </thead>
                      <tbody>
                        {invoices.map((invoice, index) => (
                          <tr key={index}>
                            <td><strong>{invoice.num_facture}</strong></td>
                            {/* La société d'abord, comme dans les deux autres onglets. */}
                            <td>{formatClientDisplay(invoice)}</td>
                            <td>{invoice.periode_mois ? `${monthNames[invoice.periode_mois - 1]} ${invoice.periode_annee}` : '—'}</td>
                            <td><strong>{formatEuros(invoice.montant_ttc)}</strong></td>
                            <td>{new Date(invoice.date_echeance).toLocaleDateString('fr-FR')}</td>
                            <td>{invoice.jours_retard > 0 ? `${invoice.jours_retard} j` : <span className="text-muted">—</span>}</td>
                            <td>{getBucketBadge(invoice.tranche, invoice.tranche_label)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </Card.Body>
            </Card>
          ) : (
            <Card>
              <Card.Body className="p-0">
                {byClient.length === 0 ? (
                  <Alert variant="success" className="m-4">
                    Aucun client en encours 🎉
                  </Alert>
                ) : (
                  <div className="table-responsive">
                    <Table striped hover className="mb-0">
                      <thead className="table-dark">
                        <tr>
                          <th>Client</th>
                          <th>Encours</th>
                          <th>Échu</th>
                          <th>&gt;30j</th>
                          <th>&gt;90j</th>
                          <th>Mois impayés</th>
                          <th>Statut</th>
                        </tr>
                      </thead>
                      <tbody>
                        {byClient.map((client) => (
                          <tr
                            key={client.client_id}
                            role="button"
                            style={{ cursor: 'pointer' }}
                            onClick={() => setSelectedClient(client)}
                          >
                            <td>{formatClientDisplay(client)}</td>
                            <td><strong>{formatEuros(client.encours)}</strong></td>
                            <td>{formatEuros(client.echu)}</td>
                            <td>{formatEuros(client.plus_30j)}</td>
                            <td>{formatEuros(client.plus_90j)}</td>
                            <td>{client.mois_impayes}</td>
                            <td>{getStatutBadge(client.statut)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </Card.Body>
            </Card>
          )}
        </>
      )}

      <Modal show={!!selectedClient} onHide={() => setSelectedClient(null)} size="xl" centered>
        <Modal.Header closeButton>
          <Modal.Title className="fs-5">
            Factures impayées — {selectedClient?.client_name}
            {selectedClient?.client_company && selectedClient.client_company !== selectedClient.client_name && (
              <span className="text-muted"> ({selectedClient.client_company})</span>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="table-responsive">
            <Table striped hover size="sm" className="mb-0">
              <thead className="table-light">
                <tr>
                  <th>N° Facture</th>
                  <th>Période</th>
                  <th>Montant TTC</th>
                  <th>Échéance</th>
                  <th>Retard</th>
                  <th>Tranche</th>
                  <th>Mails envoyés</th>
                  <th>Prochaine relance</th>
                </tr>
              </thead>
              <tbody>
                {invoices.filter((inv) => inv.client_id === selectedClient?.client_id).map((invoice, index) => (
                  <tr key={index}>
                    <td><strong>{invoice.num_facture}</strong></td>
                    <td>{invoice.periode_mois ? `${monthNames[invoice.periode_mois - 1]} ${invoice.periode_annee}` : '—'}</td>
                    <td><strong>{formatEuros(invoice.montant_ttc)}</strong></td>
                    <td>{new Date(invoice.date_echeance).toLocaleDateString('fr-FR')}</td>
                    <td>{invoice.jours_retard > 0 ? `${invoice.jours_retard} j` : <span className="text-muted">—</span>}</td>
                    <td>{getBucketBadge(invoice.tranche, invoice.tranche_label)}</td>
                    {/* Relances du drip et envois manuels depuis le BO, du plus ancien
                        au plus récent. */}
                    <td className="small">
                      {invoice.mails?.length ? (
                        invoice.mails.map((mail, i) => (
                          <div key={i}>
                            <span className="text-nowrap">{dateFr(mail.date)}</span>
                            <span className="text-muted"> — {mail.libelle}</span>
                          </div>
                        ))
                      ) : (
                        <span className="text-muted">aucun</span>
                      )}
                    </td>
                    <td className="small">{formatProchaineRelance(invoice.prochaine_relance, invoice.exclu_relance)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default Encours;
